// src/pages/admin/Sales.tsx
//
// Real sales dashboard. All figures come from the imported FileMaker
// ledger via /api/sales/*; no synthetic data.
//
// New vs the M2-05 baseline:
//   - Revenue by brand (donut + ranked list)
//   - Daily revenue for the selected year (line/area)
//   - Product sales explorer (year/month/brand/category filters,
//     group by brand, category or product)
//
// Salesperson scoping (iam-model §6) is not implemented yet: the
// invoice ledger carries no salesperson column, so this is the
// honest team rollup for all staff roles.

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { RefreshCcw, TrendingUp, BarChart3, Sparkles } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { SalesCriteriaBar } from "@/components/admin/SalesCriteriaBar";
import {
  DEFAULT_CRITERIA, readCriteriaFromURL, writeCriteriaToURL,
  toApiPayload, clearCriteria, type SalesCriteriaState,
} from "@/lib/salesCriteria";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { useAuth } from "@/components/auth/AuthContext";
import { authHeaders } from "@/lib/api";
import { formatPrice } from "@/lib/formatPrice";

interface Summary {
  invoices: number;
  total_htva: string;
  total_tva: string;
  total_tvac: string;
  avg_invoice_htva: string;
  total_discount_htva: string;
  line_sum_htva: string;
}
interface MonthPoint { month: number; invoices: number; htva: number }
interface TopCustomer { customer_id: number; name: string; invoices: number; htva: number }
interface TopProduct {
  product_id: number;
  label: string;
  brand: string;
  qty: number;
  htva: number;
}
interface YearRow { year: number; invoices: number }
interface BrandPoint { brand: string; htva: number; invoices: number }
interface DayPoint { date: string; invoices: number; htva: number }
interface ExplorerRow { key: string; label: string; brand: string; qty: number; htva: number; lines: number }

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// Distinct hues for the donut / bar chart palette (brand stack)
const BRAND_PALETTE = [
  "#2dd4bf", "#60a5fa", "#fbbf24", "#f87171", "#a78bfa",
  "#34d399", "#f59e0b", "#22d3ee", "#fb7185", "#c084fc",
  "#84cc16", "#facc15",
];

async function getJson<T>(path: string): Promise<T> {
  const r = await fetch(path, { headers: authHeaders() });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return (await r.json()) as T;
}

export function SalesDashboard() {
  const auth = useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [years, setYears] = useState<YearRow[]>([]);
  const [year, setYear] = useState<number | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [monthly, setMonthly] = useState<MonthPoint[]>([]);
  const [topCustomers, setTopCustomers] = useState<TopCustomer[]>([]);
  const [topProducts, setTopProducts] = useState<TopProduct[]>([]);
  const [byBrand, setByBrand] = useState<BrandPoint[]>([]);
  const [daily, setDaily] = useState<DayPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Criteria ribbon state (brand / year range / month range / group by).
  // Lives in the URL so Teddy can set it from the chat ("show me Jan
  // 2025 on brand Chief") and the operator can share the link.
  const [criteria, setCriteria] = useState<SalesCriteriaState>(
    readCriteriaFromURL(),
  );

  // Keep criteria + URL in sync. Re-read on browser back/forward.
  useEffect(() => {
    setCriteria(readCriteriaFromURL());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (!auth.isReady || !auth.isAuthenticated) return;
    getJson<{ years: YearRow[] }>("/api/sales/years")
      .then((d) => {
        setYears(d.years);
        if (d.years.length > 0) setYear((y) => y ?? d.years[0]!.year);
      })
      .catch((e) => setError(String(e instanceof Error ? e.message : e)));
  }, [auth.isReady, auth.isAuthenticated]);

  useEffect(() => {
    if (year == null) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    const q = `year=${year}`;
    Promise.all([
      getJson<Summary>(`/api/sales/summary?${q}`),
      getJson<{ months: MonthPoint[] }>(`/api/sales/monthly?${q}`),
      getJson<{ rows: TopCustomer[] }>(`/api/sales/top-customers?${q}&limit=8`),
      getJson<{ rows: TopProduct[] }>(`/api/sales/top-products?${q}&limit=12`),
      getJson<{ rows: BrandPoint[] }>(`/api/sales/by-brand?${q}&limit=12`),
      getJson<{ rows: DayPoint[] }>(`/api/sales/timeseries?${q}`),
    ])
      .then(([s, mo, tc, tp, bb, dd]) => {
        if (cancelled) return;
        setSummary(s);
        setMonthly(mo.months);
        setTopCustomers(tc.rows);
        setTopProducts(tp.rows);
        setByBrand(bb.rows);
        setDaily(dd.rows);
      })
      .catch((e) => {
        if (!cancelled) setError(String(e instanceof Error ? e.message : e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [year]);

  const monthlyChart = useMemo(
    () =>
      MONTH_LABELS.map((label, i) => {
        const found = monthly.find((mo) => mo.month === i + 1);
        return { label, htva: found?.htva ?? 0, invoices: found?.invoices ?? 0 };
      }),
    [monthly],
  );

  const totalBrandHtva = byBrand.reduce((s, b) => s + Number(b.htva), 0);

  // Criteria handlers. The bar edits the local state; we mirror to
  // the URL on every change so the link is shareable. We also fire
  // a `apply_criteria` request to keep the totals + the by-brand
  // donut in sync with the operator's filters — the rest of the
  // widgets (monthly / top customers / top products) still
  // use the year-scoped endpoints which are faster for those
  // single-axis views.
  function onCriteriaChange(next: SalesCriteriaState) {
    setCriteria(next);
    writeCriteriaToURL(next);
    void refreshCriteriaView(next);
  }
  function onCriteriaClear() {
    setCriteria(DEFAULT_CRITERIA);
    clearCriteria();
    void refreshCriteriaView(DEFAULT_CRITERIA);
  }
  async function refreshCriteriaView(c: SalesCriteriaState) {
    try {
      const r = await fetch("/api/admin/sales/criteria", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(toApiPayload(c)),
      });
      if (!r.ok) return;
      const d = await r.json();
      setByBrand(d.by_brand.map((b: any) => ({
        brand: b.brand, htva: b.htva, invoices: b.invoices,
      })));
      // KPI strip from totals
      if (d.totals) {
        setSummary((prev) => ({
          ...(prev ?? {} as Summary),
          invoices: d.totals.invoices,
          total_htva: String(d.totals.htva.toFixed(2)),
          total_tva: String((d.totals.tvac - d.totals.htva).toFixed(2)),
          total_tvac: String(d.totals.tvac.toFixed(2)),
        }));
      }
    } catch { /* leave existing widgets alone */ }
  }

  return (
    <div className="space-y-5">
      <SalesCriteriaBar
        criteria={criteria}
        onChange={onCriteriaChange}
        onClear={onCriteriaClear}
        years={years}
      />
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-xl font-510 tracking-tight text-fg-primary">
            <TrendingUp className="mr-2 inline h-5 w-5 text-benchmark-teal" strokeWidth={1.5} />
            {t("admin.sales.title")}
          </h1>
          <p className="mt-1 text-sm text-fg-tertiary">
            {t("admin.sales.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-fg-tertiary">{t("admin.sales.year")}</span>
          <select
            value={year ?? ""}
            onChange={(e) => setYear(Number(e.target.value))}
            className="rounded-md border border-border bg-bg-elevated px-2 py-1 text-sm text-fg-primary"
          >
            {years.map((y) => (
              <option key={y.year} value={y.year}>
                {y.year} ({y.invoices})
              </option>
            ))}
          </select>
        </div>
      </header>

      {error && (
        <div className="rounded-md border border-accent-rose/30 bg-accent-rose/10 p-3 text-sm text-accent-rose">
          {error}
        </div>
      )}

      {loading && !summary ? (
        <p className="py-8 text-center text-sm text-fg-tertiary">
          <RefreshCcw className="mr-2 inline h-4 w-4 animate-spin" />
          {t("admin.sales.loading")}
        </p>
      ) : summary ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Kpi
              label={t("admin.sales.revenueExclVat", { year })}
              value={formatPrice(Number(summary.total_htva), "EUR")}
            />
            <Kpi label={t("admin.sales.invoices")} value={String(summary.invoices)} />
            <Kpi
              label={t("admin.sales.averageInvoice")}
              value={formatPrice(Number(summary.avg_invoice_htva), "EUR")}
            />
            <Kpi
              label={t("admin.sales.revenueInclVat")}
              value={formatPrice(Number(summary.total_tvac), "EUR")}
            />
          </div>

          <p className="text-[10px] text-fg-quaternary">
            {t("admin.sales.ledgerCheck", {
              gross: formatPrice(Number(summary.line_sum_htva), "EUR"),
              net: formatPrice(Number(summary.total_htva), "EUR"),
            })}
          </p>

          <Card>
            <CardHeader>
              <CardTitle>{t("admin.sales.monthlyRevenue", { year })}</CardTitle>
              <CardDescription>{t("admin.sales.exclVatFromHeaders")}</CardDescription>
            </CardHeader>
            <CardContent className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="rgba(255,255,255,0.3)" />
                  <YAxis tick={{ fontSize: 11 }} stroke="rgba(255,255,255,0.3)"
                    tickFormatter={(v: number) => `${Math.round(v / 1000)}k`} />
                  <Tooltip
                    formatter={(value: number | string) => [
                      formatPrice(Number(value), "EUR"),
                      t("admin.sales.revenue"),
                    ]}
                    contentStyle={{ background: "#1a1d24", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }}
                  />
                  <Bar dataKey="htva" fill="#2dd4bf" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="grid gap-3 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>{t("admin.sales.dailyRevenue", { year })}</CardTitle>
                <CardDescription>{t("admin.sales.exclVatOnePointPerDay")}</CardDescription>
              </CardHeader>
              <CardContent className="h-64">
                {daily.length === 0 ? (
                  <Empty />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={daily}>
                      <defs>
                        <linearGradient id="dailyFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#2dd4bf" stopOpacity={0.6} />
                          <stop offset="100%" stopColor="#2dd4bf" stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 10 }}
                        stroke="rgba(255,255,255,0.3)"
                        tickFormatter={(d: string) => d.slice(5)}
                        interval={Math.max(0, Math.floor(daily.length / 8))}
                      />
                      <YAxis
                        tick={{ fontSize: 10 }}
                        stroke="rgba(255,255,255,0.3)"
                        tickFormatter={(v: number) => `${Math.round(v / 1000)}k`}
                      />
                      <Tooltip
                        labelFormatter={(l: string) => l}
                        formatter={(v: number | string) => [
                          formatPrice(Number(v), "EUR"),
                          t("admin.sales.revenue"),
                        ]}
                        contentStyle={{ background: "#1a1d24", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }}
                      />
                      <Area type="monotone" dataKey="htva" stroke="#2dd4bf" fill="url(#dailyFill)" />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t("admin.sales.revenueByBrand", { year })}</CardTitle>
                <CardDescription>{t("admin.sales.exclVatTop12Brands")}</CardDescription>
              </CardHeader>
              <CardContent className="h-64">
                {byBrand.length === 0 ? (
                  <Empty />
                ) : (
                  <div className="flex h-full gap-3">
                    <div className="h-full w-1/2">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={byBrand}
                            dataKey="htva"
                            nameKey="brand"
                            innerRadius={50}
                            outerRadius={80}
                            paddingAngle={2}
                          >
                            {byBrand.map((_, i) => (
                              <Cell key={i} fill={BRAND_PALETTE[i % BRAND_PALETTE.length]} />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(v: number | string) => [
                              formatPrice(Number(v), "EUR"),
                              t("admin.sales.revenue"),
                            ]}
                            contentStyle={{ background: "#1a1d24", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <ul className="h-full w-1/2 space-y-1 overflow-auto text-xs">
                      {byBrand.map((b, i) => (
                        <li key={b.brand} className="flex items-center gap-2">
                          <span
                            className="h-2 w-2 shrink-0 rounded"
                            style={{ background: BRAND_PALETTE[i % BRAND_PALETTE.length] }}
                          />
                          <span className="min-w-0 flex-1 truncate text-fg-secondary">
                            {b.brand}
                          </span>
                          <span className="font-mono text-fg-primary">
                            {formatPrice(b.htva, "EUR")}
                          </span>
                          <span className="text-[10px] text-fg-quaternary w-9 text-right">
                            {totalBrandHtva
                              ? `${Math.round((Number(b.htva) / totalBrandHtva) * 100)}%`
                              : ""}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>{t("admin.sales.topCustomers", { year })}</CardTitle>
                <CardDescription>{t("admin.sales.byRevenueExclVat")}</CardDescription>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <tbody>
                    {topCustomers.map((c, i) => (
                      <tr key={c.customer_id} className="border-b border-border/50 last:border-0">
                        <td className="py-1.5 pr-2 text-fg-quaternary">{i + 1}</td>
                        <td className="py-1.5 text-fg-primary">{c.name}</td>
                        <td className="py-1.5 text-right text-fg-tertiary">{c.invoices} inv</td>
                        <td className="py-1.5 text-right font-mono text-fg-primary">
                          {formatPrice(c.htva, "EUR")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t("admin.sales.topProducts", { year })}</CardTitle>
                <CardDescription>{t("admin.sales.byRevenueBrandProduct")}</CardDescription>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <tbody>
                    {topProducts.map((p, i) => (
                      <tr key={p.product_id} className="border-b border-border/50 last:border-0">
                        <td className="py-1.5 pr-2 text-fg-quaternary">{i + 1}</td>
                        <td className="py-1.5">
                          <div className="font-510 text-fg-primary">{p.label || `#${p.product_id}`}</div>
                          <div className="text-[10px] uppercase tracking-wider text-fg-tertiary">
                            {p.brand || "—"}
                          </div>
                        </td>
                        <td className="py-1.5 text-right text-fg-tertiary">
                          <div>× {Math.round(p.qty)}</div>
                        </td>
                        <td className="py-1.5 text-right font-mono text-fg-primary">
                          {formatPrice(p.htva, "EUR")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>

          <ProductSalesExplorer year={year} />
        </>
      ) : null}
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-fg-tertiary">{label}</div>
        <div className="mt-1 font-mono text-xl font-510 text-fg-primary">{value}</div>
      </CardContent>
    </Card>
  );
}

function Empty() {
  const { t } = useTranslation();
  return (
    <div className="flex h-full items-center justify-center text-xs text-fg-quaternary">
      <Sparkles className="mr-2 h-3.5 w-3.5" strokeWidth={1.5} />
      {t("admin.sales.noData")}
    </div>
  );
}

function ProductSalesExplorer({ year }: { year: number | null }) {
  const { t } = useTranslation();
  const [yearFilter, setYearFilter] = useState<number | null>(year);
  const [month, setMonth] = useState<number | null>(null);
  const [brand, setBrand] = useState("");
  const [category, setCategory] = useState("");
  const [groupBy, setGroupBy] = useState<"brand" | "category" | "product">("brand");
  const [rows, setRows] = useState<ExplorerRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { setYearFilter(year); }, [year]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    const handle = setTimeout(() => {
      const url = new URL("/api/sales/explorer", window.location.origin);
      if (yearFilter) url.searchParams.set("year", String(yearFilter));
      if (month) url.searchParams.set("month", String(month));
      if (brand) url.searchParams.set("brand", brand);
      if (category) url.searchParams.set("category", category);
      url.searchParams.set("group_by", groupBy);
      fetch(url.toString().replace(window.location.origin, ""), { headers: authHeaders() })
        .then(async (r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          const body = (await r.json()) as { rows: ExplorerRow[] };
          if (!cancelled) setRows(body.rows);
        })
        .catch((e) => {
          if (!cancelled) setError(String(e instanceof Error ? e.message : e));
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [yearFilter, month, brand, category, groupBy]);

  const total = rows.reduce((s, r) => s + Number(r.htva), 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <BarChart3 className="mr-2 inline h-4 w-4 text-benchmark-teal" strokeWidth={1.5} />
          {t("admin.sales.explorerTitle")}
        </CardTitle>
        <CardDescription>{t("admin.sales.explorerDescription")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t("admin.sales.year")}>
            <select
              value={yearFilter ?? ""}
              onChange={(e) => setYearFilter(e.target.value ? Number(e.target.value) : null)}
              className="w-full rounded-md border border-border bg-bg-elevated px-2 py-1 text-sm text-fg-primary"
            >
              <option value="">{t("admin.sales.all")}</option>
              {Array.from({ length: 14 }, (_, i) => 2017 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </Field>
          <Field label={t("admin.sales.month")}>
            <select
              value={month ?? ""}
              onChange={(e) => setMonth(e.target.value ? Number(e.target.value) : null)}
              className="w-full rounded-md border border-border bg-bg-elevated px-2 py-1 text-sm text-fg-primary"
            >
              <option value="">{t("admin.sales.all")}</option>
              {MONTH_LABELS.map((label, i) => (
                <option key={i + 1} value={i + 1}>{label}</option>
              ))}
            </select>
          </Field>
          <Field label={t("admin.sales.brand")}>
            <Input
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              placeholder={t("admin.sales.brandPlaceholder")}
              className="w-full"
            />
          </Field>
          <Field label={t("admin.sales.categoryLabel")}>
            <Input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder={t("admin.sales.categoryPlaceholder")}
              className="w-full"
            />
          </Field>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1 text-xs">
            <span className="text-fg-quaternary">{t("admin.sales.groupBy")}</span>
            {(["brand", "category", "product"] as const).map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGroupBy(g)}
                aria-pressed={groupBy === g}
                className={
                  "rounded px-2 py-1 font-510 uppercase tracking-wider transition-colors " +
                  (groupBy === g
                    ? "bg-benchmark-teal/20 text-benchmark-teal-bright"
                    : "bg-bg-elevated text-fg-secondary hover:bg-bg-hover hover:text-fg-primary")
                }
              >
                {g}
              </button>
            ))}
          </div>
          <span className="text-[10px] text-fg-quaternary">
            {rows.length === 1
              ? t("admin.sales.resultCount", { count: rows.length })
              : t("admin.sales.resultCountOther", { count: rows.length })}
            {" · "}
            {formatPrice(total, "EUR")} {t("admin.sales.filteredTotal")}
          </span>
        </div>
        {error && (
          <p className="rounded-md border border-accent-rose/30 bg-accent-rose/10 p-2 text-xs text-accent-rose">
            {error}
          </p>
        )}
        {loading ? (
          <p className="py-4 text-center text-xs text-fg-tertiary">
            {t("admin.sales.crunching")}
          </p>
        ) : rows.length === 0 ? (
          <p className="py-4 text-center text-xs text-fg-quaternary">
            {t("admin.sales.nothingMatches")}
          </p>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-fg-quaternary">
                  <th className="py-1 text-left">#</th>
                  <th className="py-1 text-left">
                    {groupBy === "product"
                      ? t("admin.sales.columnProduct")
                      : t("admin.sales.columnLabel")}
                  </th>
                  {groupBy !== "brand" && (
                    <th className="py-1 text-left">{t("admin.sales.brand")}</th>
                  )}
                  <th className="py-1 text-right">{t("admin.sales.columnQty")}</th>
                  <th className="py-1 text-right">{t("admin.sales.columnLines")}</th>
                  <th className="py-1 text-right">
                    {t("admin.sales.columnRevenueHtva")}
                  </th>
                  <th className="py-1 text-right">{t("admin.sales.columnShare")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.key} className="border-b border-border/40 last:border-0">
                    <td className="py-1.5 pr-2 text-fg-quaternary">{i + 1}</td>
                    <td className="py-1.5 text-fg-primary">{r.label}</td>
                    {groupBy !== "brand" && (
                      <td className="py-1.5 text-fg-secondary">{r.brand || "—"}</td>
                    )}
                    <td className="py-1.5 text-right font-mono text-fg-secondary">
                      {Math.round(r.qty)}
                    </td>
                    <td className="py-1.5 text-right text-fg-tertiary">{r.lines}</td>
                    <td className="py-1.5 text-right font-mono text-fg-primary">
                      {formatPrice(r.htva, "EUR")}
                    </td>
                    <td className="py-1.5 text-right text-[10px] text-fg-quaternary">
                      {total
                        ? `${Math.round((Number(r.htva) / total) * 100)}%`
                        : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs">
      <div className="mb-1 text-fg-tertiary">{label}</div>
      {children}
    </label>
  );
}
